import { Job, JobAction, UppercaseExportSubclasses } from '@cast/leadcast-types';

export interface AsyncJob extends Job {}

export class AsyncJob {
  get isCompleted() {
    return this.status === 'COMPLETED' || this.status === 'ERROR';
  }

  getActionSuccessMessage() {
    const uppercaseSubclass = (
      this.meta?.subclass ? this.meta?.subclass.toUpperCase() : null
    ) as UppercaseExportSubclasses;
    const key = `${this.action}${uppercaseSubclass ? `_${uppercaseSubclass}` : ''}` as const;
    const successMessage = asyncJobDisplayMessages.success[key] ?? 'Success';
    return successMessage;
  }

  getActionErrorMessage() {
    const uppercaseSubclass = (
      this.meta?.subclass ? this.meta?.subclass.toUpperCase() : null
    ) as UppercaseExportSubclasses;
    let key: string = this.action;
    if (uppercaseSubclass) {
      key = `${key}_${uppercaseSubclass}`;
    }
    const errMessage = this.meta?.message;
    return (
      errMessage ??
      asyncJobDisplayMessages.error[key] ??
      'An error occurred. Please try again later.'
    );
  }
}

type ActionWithSubclasses = `EXPORT_${UppercaseExportSubclasses}`;
type JobActionResultMessages = {
  success: Record<JobAction | ActionWithSubclasses, string>;
  error: Record<JobAction | ActionWithSubclasses, string>;
};

const asyncJobDisplayMessages: JobActionResultMessages = {
  success: {
    CREATE_BULK_COMMUNICATION_EVENT: 'Your bulk communication event has been successfully created.',
    EXPORT: 'Your download is ready. Please check your email for the exported file.',
    EXPORT_INVENTORY:
      'Your inventory download is ready. Please check your email for the exported file.',
    EXPORT_INSPECTIONS:
      'Your inspections download is ready. Please check your email for the exported file.',
    EXPORT_INSPECTIONS_TEMPLATE:
      'Your inspection template is ready. Please check your email for the exported file.',
    EXPORT_SAMPLING_POOL:
      'Your sampling pool download is ready. Please check your email for the exported file.',
    EXPORT_SAMPLING_POOL_TEMPLATE:
      'Your sampling pool template is ready. Please check your email for the exported file.',
    EXPORT_COMMUNICATIONS:
      'Your communications download is ready. Please check your email for the exported file.',
    EXPORT_COMMUNICATIONS_REPLACEMENTS:
      'Your communications replacements download is ready. Please check your email for the exported file.',
    EXPORT_REPLACEMENTS_MAILING_LIST:
      'Your replacements mailing list download is ready. Please check your email for the exported file.',
    EXPORT_CUSTOMER_CONTACTS:
      'Your customer contacts download is ready. Please check your email for the exported file.',
    EXPORT_REPLACEMENT_POOL:
      'Your replacement pool download is ready. Please check your email for the exported file.',
    EXPORT_REPLACEMENT_POOL_V2:
      'Your replacement pool download is ready. Please check your email for the exported file.',
    EXPORT_RIGHT_OF_ENTRY:
      'Your right of entry download is ready. Please check your email for the exported file.',
    EXPORT_INSPECTION_OPTIMIZER:
      'Your inspection optimizer output download is ready. Please check your email for the exported file.',
    EXPORT_PREDICT_MODEL_OUTPUT:
      'Your predict model output download is ready. Please check your email for the exported file.',
    EXPORT_PROPERTY_REGISTRATION_CODES:
      'Your property registration code download is ready. Please check your email for the exported file.',
    'BULK EDIT':
      'Your bulk edit has been successfully processed. It may take a moment for changes to be reflected in the application.',
    IMPORT_INSPECTIONS:
      'Your bulk import job has finished and the inspections were successfully imported.',
    POPULATE_COMPUTED_VALUES:
      'Computed values have successfully been updated. It may take a moment for changes to be reflected in the application.',
    PREDICT_MATERIAL_UPDATE: 'Your predict material update has successfully completed.',
    PREDICT_MATERIAL_REVERT: 'Your predict material revert has successfully completed. ',
  },
  error: {
    CREATE_BULK_COMMUNICATION_EVENT:
      'An error occurred while creating your bulk communication event. Please try again later.',
    EXPORT: 'An error occurred while downloading your data. Please try again later.',
    EXPORT_INVENTORY:
      'An error occurred while downloading your inventory data. Please try again later.',
    EXPORT_INSPECTIONS:
      'An error occurred while downloading your inspections data. Please try again later.',
    EXPORT_INSPECTIONS_TEMPLATE:
      'An error occurred while downloading your inspection template. Please try again later.',
    EXPORT_SAMPLING_POOL:
      'An error occurred while downloading your sampling pool data. Please try again later.',
    EXPORT_SAMPLING_POOL_TEMPLATE:
      'An error occurred while downloading your sampling pool template. Please try again later.',
    EXPORT_COMMUNICATIONS:
      'An error occurred while downloading your communications data. Please try again later.',
    EXPORT_COMMUNICATIONS_REPLACEMENTS:
      'An error occurred while downloading your communications replacements data. Please try again later.',
    EXPORT_REPLACEMENTS_MAILING_LIST:
      'An error occurred while downloading your replacements mailing list data. Please try again later.',
    EXPORT_CUSTOMER_CONTACTS:
      'An error occurred while downloading your customer contact data. Please try again later.',
    EXPORT_REPLACEMENT_POOL:
      'An error occurred while downloading your replacement pool data. Please try again later.',
    EXPORT_REPLACEMENT_POOL_V2:
      'An error occurred while downloading your replacement pool data. Please try again later.',
    EXPORT_RIGHT_OF_ENTRY:
      'An error occurred while downloading your right of entry data. Please try again later.',
    EXPORT_INSPECTION_OPTIMIZER:
      'An error occurred while downloading your inspection optimizer output data. Please try again later.',
    EXPORT_PREDICT_MODEL_OUTPUT:
      'An error occurred while downloading your predict model output data. Please try again later.',
    EXPORT_PROPERTY_REGISTRATION_CODES:
      'An error occurred while downloading your property registration code data. Please try again later.',
    'BULK EDIT': 'An error occurred while bulk editing. Please try again later.',
    IMPORT_INSPECTIONS:
      'An error occurred while attempting to bulk import inspections. Please try again later.',
    POPULATE_COMPUTED_VALUES:
      'An error occurred while updating computed values. Please try again later.',
    PREDICT_MATERIAL_UPDATE:
      'An error occurred while completing your predict material update. Please try again.',
    PREDICT_MATERIAL_REVERT:
      'An error occurred while completing your predict material revert. Please try again.',
  },
} as const;

export type NewJob = Pick<AsyncJob, '_id' | 'status' | 'action'>;

export interface AsyncCreatedJob {
  insertedId: string;
}
